import React, { useState } from 'react';
import { SquaresFour, CaretDown, CaretUp } from '@phosphor-icons/react';
import { useMapEngine } from './MapEngineContext';
import { MAP_MODES } from './modes/mapModes';
import { useMapStore } from '../../store/mapStore';
import type { LayerId, LayerGroup } from '../../types/mapEngine';

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

const Toggle: React.FC<{ active: boolean; onToggle: () => void }> = ({
  active,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
      active ? 'bg-[#F26430]' : 'bg-gray-600'
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        active ? 'translate-x-4' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// ---------------------------------------------------------------------------
// Color swatch
// ---------------------------------------------------------------------------

const Swatch: React.FC<{ color: string }> = ({ color }) => (
  <span
    className="w-3 h-3 rounded-sm flex-shrink-0"
    style={{ backgroundColor: color, opacity: 0.85 }}
  />
);

// ---------------------------------------------------------------------------
// Collapsible group
// ---------------------------------------------------------------------------

const LayerGroupSection: React.FC<{
  group: LayerGroup;
  layerVisibility: Record<string, boolean | undefined>;
  onToggle: (id: LayerId) => void;
}> = ({
  group,
  layerVisibility,
  onToggle,
}) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-t border-[#2a2a30] first:border-t-0">
      {/* Group header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#1c1c22] transition-colors"
      >
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
          {group.label}
        </span>
        {open ? (
          <CaretUp size={12} className="text-gray-500 flex-shrink-0" />
        ) : (
          <CaretDown size={12} className="text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Layer rows */}
      {open && (
        <div className="px-3 pb-2 space-y-2">
          {group.layers.map((layer) => {
            const visible = layerVisibility[layer.id] ?? false;
            return (
              <div key={layer.id}>
                <div className="flex items-center gap-2">
                  <Swatch color={layer.color} />
                  <span className="flex-1 text-[12px] text-gray-300 leading-tight">
                    {layer.label}
                  </span>
                  <Toggle
                    active={visible}
                    onToggle={() => onToggle(layer.id)}
                  />
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

const MapLayerPanel: React.FC = () => {
  const { mode, layerVisibility, setLayerVisibility } = useMapEngine();
  const [panelOpen, setPanelOpen] = useState(true);

  const modeConfig = MAP_MODES[mode];
  const groups = modeConfig.layerGroups;

  const handleToggle = (id: LayerId) => {
    setLayerVisibility(id, !(layerVisibility[id] ?? false));
  };

  return (
    <div
      className="absolute top-3 right-3 z-10 rounded-xl shadow-lg border overflow-hidden"
      style={{
        background: '#141418',
        borderColor: '#2a2a30',
        minWidth: 220,
      }}
    >
      {/* Panel header */}
      <button
        type="button"
        onClick={() => setPanelOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-[#1c1c22] transition-colors"
      >
        <div className="flex items-center gap-2">
          <SquaresFour size={15} className="text-[#F26430] flex-shrink-0" weight="bold" />
          <span className="text-[12px] font-semibold text-gray-200">Capas</span>
        </div>
        {panelOpen ? (
          <CaretUp size={12} className="text-gray-500 flex-shrink-0" />
        ) : (
          <CaretDown size={12} className="text-gray-500 flex-shrink-0" />
        )}
      </button>

      {/* Layer groups */}
      {panelOpen && (
        <div>
          {groups.map((group) => (
            <LayerGroupSection
              key={group.id}
              group={group}
              layerVisibility={layerVisibility}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MapLayerPanel;
